import type { AgentView, Snapshot, Status } from "../shared/contracts";
import { INTENT_LABELS, STATUS_LABELS } from "../shared/contracts";
import { effectiveStatus, isOnline, presenceDescription } from "./presence";

export type DispatchFilter =
  Status | "online" | "ready" | "away" | "unknown_offline";
export const awayStatuses: Status[] = ["break", "lunch", "training", "meeting"];
export const monitoredAgents = (agents: AgentView[]): AgentView[] =>
  agents.filter((agent) => agent.activityMonitoringEnabled !== false);
export type WaitBand = "short" | "steady" | "long" | "urgent";
export function waitBand(seconds: number): WaitBand {
  return seconds < 120
    ? "short"
    : seconds < 300
      ? "steady"
      : seconds < 600
        ? "long"
        : "urgent";
}

export function statusSeconds(stateSince: number, now: number): number | null {
  if (!Number.isFinite(stateSince) || !Number.isFinite(now) || stateSince > now)
    return null;
  return Math.max(0, Math.floor((now - stateSince) / 1000));
}

export function statusClock(stateSince: number, now: number): string | null {
  const seconds = statusSeconds(stateSince, now);
  if (seconds === null) return null;
  const minutes = Math.floor(seconds / 60);
  const remainder = String(seconds % 60).padStart(2, "0");
  return minutes < 60
    ? `${String(minutes).padStart(2, "0")}:${remainder}`
    : `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:${remainder}`;
}

export function availableWaitBand(
  agent: AgentView,
  now: number,
  disconnected = false,
): WaitBand | null {
  if (effectiveStatus(agent, now, disconnected) !== "available") return null;
  const seconds = statusSeconds(agent.stateSince, now);
  if (seconds === null) return null;
  return waitBand(seconds);
}

export function compareDispatchAgents(
  a: AgentView,
  b: AgentView,
  now: number,
  disconnected: boolean,
  teamName: (id: string) => string,
): number {
  const priority = (agent: AgentView) => {
    const status = effectiveStatus(agent, now, disconnected);
    return status === "available"
      ? 0
      : status === "busy"
        ? 1
        : status === "wrap_up"
          ? 2
          : status === "reserved"
            ? 3
            : status === "unknown"
              ? 5
              : status === "offline"
                ? 6
                : 4;
  };
  const difference = priority(a) - priority(b);
  if (difference) return difference;
  if (priority(a) === 0) {
    const aSince =
      statusSeconds(a.stateSince, now) === null ? Infinity : a.stateSince;
    const bSince =
      statusSeconds(b.stateSince, now) === null ? Infinity : b.stateSince;
    if (aSince !== bSince) return aSince - bSince;
  }
  return (
    teamName(a.teamIds[0] ?? "").localeCompare(teamName(b.teamIds[0] ?? "")) ||
    a.name.localeCompare(b.name) ||
    a.id.localeCompare(b.id)
  );
}
const transferParticipant = (agent: Pick<AgentView, "duty" | "role">) =>
  agent.duty === "opener" ||
  agent.duty === "closer" ||
  agent.role === "lead" ||
  agent.role === "manager" ||
  agent.role === "admin";

export function readyForTransfer(
  agent: AgentView,
  snapshot: Snapshot,
  now: number,
  disconnected = false,
) {
  return (
    agent.activityMonitoringEnabled !== false &&
    transferParticipant(agent) &&
    agent.source !== "none" &&
    effectiveStatus(agent, now, disconnected) === "available" &&
    !snapshot.transfers.some(
      (transfer) =>
        transfer.toUserId === agent.id &&
        (transfer.state === "pending" || transfer.state === "accepted") &&
        transfer.expiresAt > now,
    )
  );
}

export function canRequestHandoff(
  agent: AgentView,
  snapshot: Snapshot,
  now: number,
  disconnected = false,
) {
  const viewer = snapshot.viewer;
  if (
    !transferParticipant(viewer) ||
    agent.id === viewer.id ||
    !readyForTransfer(agent, snapshot, now, disconnected)
  )
    return false;
  if (viewer.role === "admin") return true;
  const shared = viewer.teamIds.filter((id) => agent.teamIds.includes(id));
  if (viewer.role === "manager") return shared.length > 0;
  return (
    viewer.role === "lead" &&
    snapshot.teams.some((team) => team.pilotEnabled && shared.includes(team.id))
  );
}

export function matchesDispatchFilter(
  agent: AgentView,
  filter: string,
  snapshot: Snapshot,
  now: number,
  disconnected = false,
) {
  if (agent.activityMonitoringEnabled === false) return false;
  const status = effectiveStatus(agent, now, disconnected);
  if (!filter) return true;
  if (filter === "online") return isOnline(agent, now, disconnected);
  if (filter === "ready")
    return readyForTransfer(agent, snapshot, now, disconnected);
  if (filter === "away") return awayStatuses.includes(status);
  if (filter === "unknown_offline")
    return status === "unknown" || status === "offline";
  return status === filter;
}

export function dispatchLabel(
  agent: AgentView,
  now: number,
  disconnected = false,
) {
  const status = effectiveStatus(agent, now, disconnected);
  return status === "available"
    ? "Available"
    : status === "busy"
      ? "On a call"
      : status === "wrap_up"
        ? "In wrap-up"
        : STATUS_LABELS[status];
}

export function summarizeSnapshot(
  snapshot: Snapshot,
  now: number,
  disconnected = false,
) {
  const agents = {
    ready: 0,
    available: 0,
    busy: 0,
    wrapUp: 0,
    reserved: 0,
    away: 0,
    unknown: 0,
    offline: 0,
    unknownOffline: 0,
  };
  for (const agent of monitoredAgents(snapshot.agents)) {
    const status = effectiveStatus(agent, now, disconnected);
    if (readyForTransfer(agent, snapshot, now, disconnected)) agents.ready++;
    if (status === "available") agents.available++;
    else if (status === "busy") agents.busy++;
    else if (status === "wrap_up") agents.wrapUp++;
    else if (status === "reserved") agents.reserved++;
    else if (awayStatuses.includes(status)) agents.away++;
    else if (status === "unknown") agents.unknown++;
    else if (status === "offline") agents.offline++;
    if (status === "unknown" || status === "offline") agents.unknownOffline++;
  }
  const handoffs = { pending: 0, accepted: 0, expired: 0, completed: 0 };
  for (const transfer of snapshot.transfers) {
    const state =
      (transfer.state === "pending" || transfer.state === "accepted") &&
      transfer.expiresAt <= now
        ? "expired"
        : transfer.state;
    if (state in handoffs) handoffs[state as keyof typeof handoffs]++;
  }
  const dueCallbacks = snapshot.queueItems.filter(
    (item) =>
      item.status === "open" && item.dueAt !== null && item.dueAt <= now,
  ).length;
  return { agents, handoffs, dueCallbacks };
}

export function teamCapacity(
  snapshot: Snapshot,
  now: number,
  disconnected = false,
) {
  return snapshot.teams.map((team) => {
    const agents = monitoredAgents(snapshot.agents).filter((agent) =>
      agent.teamIds.includes(team.id),
    );
    const summary = summarizeSnapshot(
      { ...snapshot, agents },
      now,
      disconnected,
    );
    return { team, total: agents.length, ...summary.agents };
  });
}

export function agentWorkContext(
  agent: AgentView,
  snapshot: Snapshot,
  now: number,
) {
  const queue = snapshot.queueItems.filter(
    (item) => item.ownerId === agent.id && item.status === "open",
  );
  const due = queue.filter((item) => item.dueAt !== null && item.dueAt <= now);
  const next = queue
    .filter((item) => item.dueAt !== null)
    .sort((a, b) => a.dueAt! - b.dueAt!)[0];
  const handoffs = snapshot.transfers.filter(
    (transfer) =>
      (transfer.fromUserId === agent.id || transfer.toUserId === agent.id) &&
      (transfer.state === "pending" || transfer.state === "accepted") &&
      transfer.expiresAt > now,
  );
  return { open: queue.length, due: due.length, next, handoffs };
}

function csvCell(value: string): string {
  const neutral = /^[\s\uFEFF]*[=+\-@]/u.test(value) ? "'" + value : value;
  return '"' + neutral.replaceAll('"', '""') + '"';
}

export function rosterCsv(
  agents: AgentView[],
  teamName: (id: string) => string,
  snapshot: Snapshot,
  stale: boolean,
  now = snapshot.serverTime,
): string {
  const rows = [
    [
      "GHL Connect visible roster",
      snapshot.environment === "demo"
        ? "Fictional demo data"
        : "Authorized snapshot",
      new Date(snapshot.serverTime).toISOString(),
    ],
    ["Name", "Team", "Activity", "Intent", "Observed at", "Observation detail"],
    ...monitoredAgents(agents).map((agent) => [
      agent.name,
      agent.teamIds.map(teamName).join(", ") || "No team",
      dispatchLabel(agent, now, stale),
      INTENT_LABELS[agent.intent],
      agent.lastSeen ? new Date(agent.lastSeen).toISOString() : "Not observed",
      presenceDescription(agent, now, stale),
    ]),
  ];
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}
